import { X } from '@phosphor-icons/react';
import { useEffect, useLayoutEffect, useRef } from 'react';
import { usePinchZoom } from '../../hooks/usePinchZoom';
import { t } from '../../i18n';

/**
 * Une photo en plein ecran.
 *
 * Le fond noir n'emprunte rien au theme du spot : une photo se regarde
 * sur du neutre, et c'est la seule exception assumee a la palette.
 * N'importe quel appui referme — c'est ce qu'on attend d'une photo
 * ouverte en grand.
 *
 * On l'agrandit a deux doigts, ou d'un double appui : `usePinchZoom` dit
 * pourquoi le navigateur ne s'en charge pas tout seul.
 */
export function PhotoViewer({
  src,
  alt = '',
  from,
  onClose,
}: {
  /** `null` ferme la visionneuse : l'appelant n'a qu'un etat a tenir. */
  src: string | null;
  alt?: string;
  /**
   * La place qu'occupait la vignette au moment de l'appui.
   *
   * Quand elle est donnee, la photo part de la et grandit jusqu'a sa
   * place definitive : c'est la meme image qui se deplace, et non une
   * nouvelle qui apparait. Absente, on retombe sur un simple
   * agrandissement depuis le centre.
   */
  from?: DOMRect | null;
  onClose: () => void;
}) {
  useEffect(() => {
    if (!src) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    document.documentElement.classList.add('sheet-open');

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.documentElement.classList.remove('sheet-open');
    };
  }, [src, onClose]);

  if (!src) return null;

  /*
    La vue est un composant a part, monte avec la photo et demonte avec
    elle. C'est ce qui fait tenir le zoom : ses ecouteurs se posent sur
    une zone qui existe deja — poses plus haut, ils n'auraient rien
    trouve tant qu'aucune photo n'etait ouverte. Et la cle par source
    remet la photo suivante a sa taille d'origine.
  */
  return <Vue key={src} src={src} alt={alt} from={from ?? null} onClose={onClose} />;
}

function Vue({
  src,
  alt,
  from,
  onClose,
}: {
  src: string;
  alt: string;
  from: DOMRect | null;
  onClose: () => void;
}) {
  const zoneRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  usePinchZoom(zoneRef, imageRef);

  /*
    La photo part de la vignette et rejoint sa place.

    Un agrandissement depuis le centre a beau etre lent et bien courbe,
    il ne raconte rien : l'oeil ne relie pas la vignette qu'il vient de
    toucher a l'image qui parait ailleurs. Le mouvement doit partir de la
    ou le doigt s'est pose. C'est ce que fait Instagram, et c'est ce que
    montre le releve image par image de la video de reference : la
    vignette s'agrandit et remonte, en deux dixiemes de seconde.

    Rien de tout cela ne peut se dire dans une feuille de style : la
    position de depart change a chaque photo. On la joue donc a la main,
    une seule fois, quand la balise vient d'apparaitre.
  */
  useLayoutEffect(() => {
    const noeud = imageRef.current;
    if (!noeud || !from) return;
    const vers = noeud.getBoundingClientRect();
    if (!vers.width || !vers.height) return;

    const ex = from.width / vers.width;
    const ey = from.height / vers.height;
    const dx = from.left + from.width / 2 - (vers.left + vers.width / 2);
    const dy = from.top + from.height / 2 - (vers.top + vers.height / 2);

    noeud.animate(
      [{ transform: `translate(${dx}px, ${dy}px) scale(${ex}, ${ey})` }, { transform: 'none' }],
      /*
        Deux cent vingt millisecondes, et une courbe qui freine fort : le
        releve de la video donne la moitie du chemin dans les trente
        premieres, les neuf dixiemes a mi-course. C'est ce qui donne
        l'elan — une vitesse constante ferait glisser l'image comme un
        tiroir.
      */
      { duration: 220, easing: 'cubic-bezier(0.17, 0.89, 0.3, 1)' },
    );
  }, [from]);

  return (
    <div
      className="viewer"
      role="dialog"
      aria-modal="true"
      aria-label={t.commun.pleinEcran}
      ref={zoneRef}
    >
      <button className="viewer__scrim" aria-label={t.commun.fermer} onClick={onClose} />
      {/*
        L'agrandissement depuis le centre ne sert que faute de vignette
        connue : quand elle l'est, c'est l'animation jouee ci-dessus qui
        porte le mouvement, et celle de la feuille la gênerait.
      */}
      <img
        className={`viewer__image${from ? ' viewer__image--depuis' : ''}`}
        src={src}
        alt={alt}
        ref={imageRef}
      />
      <button className="viewer__close" aria-label={t.commun.fermer} onClick={onClose}>
        <X size={22} weight="bold" />
      </button>
    </div>
  );
}
